import { supabase } from '../lib/supabase';

/**
 * Credit Notes Service - Handles all credit note operations
 * Fixed version with proper error handling, validation, and database compatibility
 */

// Credit Note Status Constants
export const CREDIT_NOTE_STATUS = {
  DRAFT: 'draft',
  ISSUED: 'issued', 
  APPLIED: 'applied',
  CANCELLED: 'cancelled',
  VOID: 'void'
};

// Credit Note Reason Constants  
export const CREDIT_REASONS = {
  PRODUCT_RETURN: 'product_return',
  PRICING_ADJUSTMENT: 'pricing_adjustment', // Fixed to match database enum
  SERVICE_ISSUE: 'service_issue', // Fixed to match database enum
  OVERPAYMENT: 'overpayment', // Fixed to match database enum
  DISCOUNT: 'discount', // Fixed to match database enum
  OTHER: 'other'
};

/**
 * Validates user authentication
 * @throws {Error} If user is not authenticated
 * @returns {Object} Authenticated user object
 */
const validateAuth = async () => {
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error) throw new Error(`Authentication error: ${error.message}`);
  if (!user) throw new Error('Authentication required. Please log in to continue.');
  return user;
};

/**
 * Handles and formats database errors
 * @param {Object} error - Supabase error object
 * @param {string} operation - The operation that failed
 * @throws {Error} Formatted error message
 */
const handleDatabaseError = (error, operation) => {
  console.error(`Credit Notes Service - ${operation} failed:`, error);
  
  if (error?.code === 'PGRST116') {
    throw new Error(`${operation} failed: No data found or access denied.`);
  }
  
  if (error?.code === '23505') {
    throw new Error(`${operation} failed: Duplicate entry detected.`);
  }
  
  if (error?.code === '23503') {
    throw new Error(`${operation} failed: Referenced record not found.`);
  }
  
  if (error?.code === '42703') {
    throw new Error(`${operation} failed: Invalid column reference.`);
  }
  
  throw new Error(`${operation} failed: ${error?.message || 'Unknown database error'}`);
};

/**
 * Validates required fields for credit note creation
 * @param {Object} creditNoteData - Credit note data to validate
 * @throws {Error} If validation fails
 */
const validateCreditNoteData = (creditNoteData) => {
  const required = ['customerId', 'creditReason', 'totalCreditAmount'];
  const missing = required?.filter(field => !creditNoteData?.[field]);
  
  if (missing?.length > 0) {
    throw new Error(`Missing required fields: ${missing.join(', ')}`);
  }
  
  if (creditNoteData?.totalCreditAmount <= 0) {
    throw new Error('Total credit amount must be greater than zero');
  }
  
  if (!Object.values(CREDIT_REASONS)?.includes(creditNoteData?.creditReason)) {
    throw new Error('Invalid credit reason provided');
  }
};

export const creditNotesService = {
  /**
   * Retrieves all credit notes for an organization with enhanced data structure
   * @param {string} organizationId - Organization identifier
   * @returns {Promise<Array>} Array of formatted credit notes
   * @throws {Error} If retrieval fails
   */
  async getAll(organizationId) {
    try {
      const user = await validateAuth();
      
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      // Fixed query to match actual database schema
      const { data, error } = await supabase
        ?.from('credit_notes')
        ?.select(`
          *,
          customers (
            id,
            customer_name,
            customer_number,
            email,
            phone
          ),
          credit_note_line_items (
            id,
            item_description,
            quantity,
            unit_price,
            tax_rate,
            tax_amount,
            line_total,
            notes,
            line_number,
            items (
              item_name,
              item_number,
              unit_of_measure
            )
          )
        `)
        ?.eq('organization_id', organizationId)
        ?.order('created_at', { ascending: false });

      if (error) handleDatabaseError(error, 'Fetching credit notes');

      // Enhanced data transformation with better structure
      return data?.map(creditNote => ({
        id: creditNote?.id,
        creditNoteNumber: creditNote?.credit_note_number,
        customerId: creditNote?.customer_id,
        customer: creditNote?.customers ? {
          id: creditNote?.customers?.id,
          customerName: creditNote?.customers?.customer_name,
          customerNumber: creditNote?.customers?.customer_number,
          email: creditNote?.customers?.email,
          phone: creditNote?.customers?.phone
        } : null,
        creditReason: creditNote?.credit_reason,
        reasonDescription: creditNote?.reason_description,
        issueDate: creditNote?.issue_date,
        issuedAt: creditNote?.issued_at,
        status: creditNote?.status,
        statusDisplay: this.getStatusDisplay(creditNote?.status),
        statusColor: this.getStatusColor(creditNote?.status),
        subtotal: parseFloat(creditNote?.subtotal || 0),
        taxAmount: parseFloat(creditNote?.tax_amount || 0),
        totalCreditAmount: parseFloat(creditNote?.total_credit_amount || 0),
        appliedAmount: parseFloat(creditNote?.applied_amount || 0),
        appliedAt: creditNote?.applied_at,
        remainingCredit: parseFloat(creditNote?.remaining_credit || creditNote?.total_credit_amount || 0),
        description: creditNote?.description,
        notes: creditNote?.notes,
        currencyCode: creditNote?.currency_code || 'USD',
        createdAt: creditNote?.created_at,
        updatedAt: creditNote?.updated_at,
        canEdit: creditNote?.status === CREDIT_NOTE_STATUS?.DRAFT,
        canDelete: creditNote?.status === CREDIT_NOTE_STATUS?.DRAFT,
        canApply: creditNote?.status === CREDIT_NOTE_STATUS?.ISSUED && parseFloat(creditNote?.remaining_credit || 0) > 0,
        // Enhanced line items with better formatting
        lineItems: creditNote?.credit_note_line_items
          ?.sort((a, b) => (a?.line_number || 0) - (b?.line_number || 0))
          ?.map((item) => ({
            id: item?.id,
            lineNumber: item?.line_number || 1,
            itemDescription: item?.item_description,
            quantity: parseFloat(item?.quantity || 0),
            unitPrice: parseFloat(item?.unit_price || 0),
            taxRate: parseFloat(item?.tax_rate || 0),
            taxAmount: parseFloat(item?.tax_amount || 0),
            lineTotal: parseFloat(item?.line_total || 0),
            notes: item?.notes,
            item: item?.items ? {
              itemName: item?.items?.item_name,
              itemNumber: item?.items?.item_number,
              unitOfMeasure: item?.items?.unit_of_measure
            } : null
          })) || [],
        // Add thumbnail image for visual reference
        thumbnailImage: '/assets/images/image-1763218474354.png',
        imageAlt: 'Credit note document thumbnail showing financial summary and line items'
      })) || [];
    } catch (error) {
      console.error('Error in creditNotesService.getAll:', error);
      throw error;
    }
  },

  /**
   * Retrieves active customers for credit note creation
   * @param {string} organizationId - Organization identifier
   * @returns {Promise<Array>} Array of customer objects
   */
  async getCustomers(organizationId) {
    try {
      const user = await validateAuth();
      
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { data, error } = await supabase
        ?.from('customers')
        ?.select('id, customer_name, customer_number, email, phone, status, total_outstanding')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active')
        ?.order('customer_name');

      if (error) handleDatabaseError(error, 'Fetching customers');

      return data?.map(customer => ({
        id: customer?.id,
        customerName: customer?.customer_name,
        customerNumber: customer?.customer_number,
        email: customer?.email,
        phone: customer?.phone,
        status: customer?.status,
        totalOutstanding: parseFloat(customer?.total_outstanding || 0),
        displayName: `${customer?.customer_name} (${customer?.customer_number})`
      })) || [];
    } catch (error) {
      console.error('Error in creditNotesService.getCustomers:', error);
      throw error;
    }
  },

  /**
   * Retrieves active items for credit note line items
   * @param {string} organizationId - Organization identifier
   * @returns {Promise<Array>} Array of item objects
   */
  async getItems(organizationId) {
    try {
      const user = await validateAuth();
      
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { data, error } = await supabase
        ?.from('items')
        ?.select('id, item_name, item_number, selling_price, tax_rate, unit_of_measure, description, is_active')
        ?.eq('organization_id', organizationId)
        ?.eq('is_active', true)
        ?.order('item_name');

      if (error) handleDatabaseError(error, 'Fetching items');

      return data?.map(item => ({
        id: item?.id,
        itemName: item?.item_name,
        itemNumber: item?.item_number,
        sellingPrice: parseFloat(item?.selling_price || 0),
        taxRate: parseFloat(item?.tax_rate || 0),
        unitOfMeasure: item?.unit_of_measure,
        description: item?.description,
        displayName: `${item?.item_name} (${item?.item_number})`,
        isActive: item?.is_active
      })) || [];
    } catch (error) {
      console.error('Error in creditNotesService.getItems:', error);
      throw error;
    }
  },

  /**
   * Creates a new credit note with comprehensive validation
   * @param {Object} creditNoteData - Credit note data
   * @param {string} organizationId - Organization identifier
   * @returns {Promise<Object>} Created credit note summary
   */
  async create(creditNoteData, organizationId) {
    try {
      const user = await validateAuth();
      
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      
      // Validate input data
      validateCreditNoteData(creditNoteData);

      // Generate credit note number first
      const { data: creditNoteNumber, error: numberError } = await supabase
        ?.rpc('generate_credit_note_number', { org_id: organizationId });

      if (numberError) handleDatabaseError(numberError, 'Generating credit note number');

      // Enhanced payload with better defaults and required fields
      const creditNotePayload = {
        organization_id: organizationId,
        created_by: user?.id,
        customer_id: creditNoteData?.customerId,
        credit_note_number: creditNoteNumber,
        credit_reason: creditNoteData?.creditReason,
        reason_description: creditNoteData?.reasonDescription || '',
        issue_date: creditNoteData?.issueDate || new Date()?.toISOString()?.split('T')?.[0],
        status: creditNoteData?.status || CREDIT_NOTE_STATUS?.DRAFT,
        subtotal: parseFloat(creditNoteData?.subtotal || 0),
        tax_amount: parseFloat(creditNoteData?.taxAmount || 0),
        total_credit_amount: parseFloat(creditNoteData?.totalCreditAmount || 0),
        description: creditNoteData?.description || '',
        notes: creditNoteData?.notes || '',
        currency_code: creditNoteData?.currencyCode || 'USD',
        reference_invoice_id: creditNoteData?.referenceInvoiceId || null
      };

      const { data: creditNote, error: creditNoteError } = await supabase
        ?.from('credit_notes')
        ?.insert(creditNotePayload)
        ?.select()
        ?.single();

      if (creditNoteError) handleDatabaseError(creditNoteError, 'Creating credit note');

      // Create line items with enhanced validation
      if (creditNoteData?.lineItems && Array.isArray(creditNoteData?.lineItems) && creditNoteData?.lineItems?.length > 0) {
        const lineItemsPayload = creditNoteData?.lineItems?.map((item, index) => {
          // Validate line item data
          if (!item?.itemDescription) {
            throw new Error(`Line item ${index + 1}: Item description is required`);
          }
          if (!item?.quantity || item?.quantity <= 0) {
            throw new Error(`Line item ${index + 1}: Quantity must be greater than zero`);
          }
          if (item?.unitPrice === undefined || item?.unitPrice < 0) {
            throw new Error(`Line item ${index + 1}: Unit price must be zero or greater`);
          }

          const quantity = parseFloat(item?.quantity || 0);
          const unitPrice = parseFloat(item?.unitPrice || 0);
          const taxRate = parseFloat(item?.taxRate || 0);
          const lineTotal = quantity * unitPrice;
          const taxAmount = (lineTotal * taxRate) / 100;

          return {
            credit_note_id: creditNote?.id,
            line_number: index + 1,
            item_id: item?.itemId || null,
            account_id: item?.accountId || null,
            item_description: item?.itemDescription,
            quantity: quantity,
            unit_price: unitPrice,
            tax_rate: taxRate,
            tax_amount: taxAmount,
            line_total: lineTotal,
            notes: item?.notes || ''
          };
        });

        const { error: lineItemsError } = await supabase
          ?.from('credit_note_line_items')
          ?.insert(lineItemsPayload);

        if (lineItemsError) handleDatabaseError(lineItemsError, 'Creating credit note line items');
      }

      return {
        id: creditNote?.id,
        creditNoteNumber: creditNote?.credit_note_number,
        customerId: creditNote?.customer_id,
        status: creditNote?.status,
        statusDisplay: this.getStatusDisplay(creditNote?.status),
        totalCreditAmount: parseFloat(creditNote?.total_credit_amount || 0),
        remainingCredit: parseFloat(creditNote?.total_credit_amount || 0), // Initial remaining equals total
        createdAt: creditNote?.created_at,
        success: true,
        message: 'Credit note created successfully'
      };
    } catch (error) {
      console.error('Error in creditNotesService.create:', error);
      throw error;
    }
  },

  /**
   * Updates credit note status with enhanced validation
   * @param {string} creditNoteId - Credit note identifier
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated credit note summary
   */
  async updateStatus(creditNoteId, status) {
    try {
      const user = await validateAuth();
      
      if (!creditNoteId) {
        throw new Error('Credit note ID is required');
      }
      
      if (!Object.values(CREDIT_NOTE_STATUS)?.includes(status)) {
        throw new Error(`Invalid status: ${status}`);
      }

      const updateData = { 
        status,
        updated_at: new Date()?.toISOString()
      };
      
      // Add issued_at when status changes to issued
      if (status === CREDIT_NOTE_STATUS?.ISSUED) {
        updateData.issued_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase
        ?.from('credit_notes')
        ?.update(updateData)
        ?.eq('id', creditNoteId)
        ?.select()
        ?.single();

      if (error) handleDatabaseError(error, 'Updating credit note status');

      return {
        id: data?.id,
        status: data?.status,
        statusDisplay: this.getStatusDisplay(data?.status),
        statusColor: this.getStatusColor(data?.status),
        issuedAt: data?.issued_at,
        updatedAt: data?.updated_at,
        success: true,
        message: `Credit note status updated to ${this.getStatusDisplay(data?.status)}`
      };
    } catch (error) {
      console.error('Error in creditNotesService.updateStatus:', error);
      throw error;
    }
  },

  /**
   * Applies credit note to reduce outstanding balance
   * @param {string} creditNoteId - Credit note identifier
   * @param {number} appliedAmount - Amount to apply
   * @returns {Promise<Object>} Updated credit note summary
   */
  async applyCredit(creditNoteId, appliedAmount) {
    try {
      const user = await validateAuth();
      
      if (!creditNoteId) {
        throw new Error('Credit note ID is required');
      }
      
      if (!appliedAmount || appliedAmount <= 0) {
        throw new Error('Applied amount must be greater than zero');
      }

      // Get current credit note to validate available credit
      const { data: currentCredit, error: fetchError } = await supabase
        ?.from('credit_notes')
        ?.select('remaining_credit, total_credit_amount, applied_amount, status')
        ?.eq('id', creditNoteId)
        ?.single();

      if (fetchError) handleDatabaseError(fetchError, 'Fetching credit note for application');

      const remainingCredit = parseFloat(currentCredit?.remaining_credit || currentCredit?.total_credit_amount || 0);
      const currentApplied = parseFloat(currentCredit?.applied_amount || 0);

      if (appliedAmount > remainingCredit) {
        throw new Error(`Cannot apply $${appliedAmount}. Only $${remainingCredit} remaining credit available.`);
      }

      const newAppliedAmount = currentApplied + appliedAmount;
      const newRemainingCredit = remainingCredit - appliedAmount;
      const newStatus = newRemainingCredit <= 0 ? CREDIT_NOTE_STATUS?.APPLIED : CREDIT_NOTE_STATUS?.ISSUED;

      const { data, error } = await supabase
        ?.from('credit_notes')
        ?.update({
            applied_amount: newAppliedAmount,
            remaining_credit: newRemainingCredit,
            applied_at: new Date()?.toISOString(),
            status: newStatus,
            updated_at: new Date()?.toISOString()
          })
        ?.eq('id', creditNoteId)
        ?.select()
        ?.single();

      if (error) handleDatabaseError(error, 'Applying credit note');

      return {
        id: data?.id,
        appliedAmount: parseFloat(data?.applied_amount || 0),
        appliedAt: data?.applied_at,
        remainingCredit: parseFloat(data?.remaining_credit || 0),
        status: data?.status,
        statusDisplay: this.getStatusDisplay(data?.status),
        statusColor: this.getStatusColor(data?.status),
        success: true,
        message: `Successfully applied $${appliedAmount} credit. Remaining: $${parseFloat(data?.remaining_credit || 0)}`
      };
    } catch (error) {
      console.error('Error in creditNotesService.applyCredit:', error);
      throw error;
    }
  },

  /**
   * Deletes a credit note (only draft status allowed)
   * @param {string} creditNoteId - Credit note identifier
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(creditNoteId) {
    try {
      const user = await validateAuth();
      
      if (!creditNoteId) {
        throw new Error('Credit note ID is required');
      }

      // Check if credit note exists and is in draft status
      const { data: creditNote, error: checkError } = await supabase
        ?.from('credit_notes')
        ?.select('status, credit_note_number')
        ?.eq('id', creditNoteId)
        ?.single();

      if (checkError) handleDatabaseError(checkError, 'Fetching credit note for deletion');
      
      if (!creditNote) {
        throw new Error('Credit note not found');
      }
      
      if (creditNote?.status !== CREDIT_NOTE_STATUS?.DRAFT) {
        throw new Error(`Cannot delete credit note. Only ${this.getStatusDisplay(CREDIT_NOTE_STATUS.DRAFT)} credit notes can be deleted.`);
      }

      // Delete line items first (will cascade automatically due to foreign key)
      const { error } = await supabase
        ?.from('credit_notes')
        ?.delete()
        ?.eq('id', creditNoteId);

      if (error) handleDatabaseError(error, 'Deleting credit note');
      
      return { 
        success: true, 
        message: `Credit note ${creditNote?.credit_note_number} deleted successfully`,
        deletedId: creditNoteId
      };
    } catch (error) {
      console.error('Error in creditNotesService.delete:', error);
      throw error;
    }
  },

  /**
   * Gets single credit note by ID
   * @param {string} creditNoteId - Credit note identifier
   * @returns {Promise<Object>} Credit note details
   */
  async getById(creditNoteId) {
    try {
      const user = await validateAuth();
      
      if (!creditNoteId) {
        throw new Error('Credit note ID is required');
      }

      const { data, error } = await supabase
        ?.from('credit_notes')
        ?.select(`
          *,
          customers (
            id,
            customer_name,
            customer_number,
            email,
            phone
          ),
          credit_note_line_items (
            id,
            item_description,
            quantity,
            unit_price,
            tax_rate,
            tax_amount,
            line_total,
            notes,
            line_number,
            items (
              item_name,
              item_number,
              unit_of_measure
            )
          )
        `)
        ?.eq('id', creditNoteId)
        ?.single();

      if (error) handleDatabaseError(error, 'Fetching credit note');
      
      if (!data) {
        throw new Error('Credit note not found');
      }

      return {
        id: data?.id,
        creditNoteNumber: data?.credit_note_number,
        customerId: data?.customer_id,
        customer: data?.customers ? {
          id: data?.customers?.id,
          customerName: data?.customers?.customer_name,
          customerNumber: data?.customers?.customer_number,
          email: data?.customers?.email,
          phone: data?.customers?.phone
        } : null,
        creditReason: data?.credit_reason,
        reasonDescription: data?.reason_description,
        issueDate: data?.issue_date,
        issuedAt: data?.issued_at,
        status: data?.status,
        statusDisplay: this.getStatusDisplay(data?.status),
        statusColor: this.getStatusColor(data?.status),
        subtotal: parseFloat(data?.subtotal || 0),
        taxAmount: parseFloat(data?.tax_amount || 0),
        totalCreditAmount: parseFloat(data?.total_credit_amount || 0),
        appliedAmount: parseFloat(data?.applied_amount || 0),
        appliedAt: data?.applied_at,
        remainingCredit: parseFloat(data?.remaining_credit || data?.total_credit_amount || 0),
        description: data?.description,
        notes: data?.notes,
        currencyCode: data?.currency_code || 'USD',
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        canEdit: data?.status === CREDIT_NOTE_STATUS?.DRAFT,
        canDelete: data?.status === CREDIT_NOTE_STATUS?.DRAFT,
        canApply: data?.status === CREDIT_NOTE_STATUS?.ISSUED && parseFloat(data?.remaining_credit || 0) > 0,
        lineItems: data?.credit_note_line_items
          ?.sort((a, b) => (a?.line_number || 0) - (b?.line_number || 0))
          ?.map((item) => ({
            id: item?.id,
            lineNumber: item?.line_number || 1,
            itemDescription: item?.item_description,
            quantity: parseFloat(item?.quantity || 0),
            unitPrice: parseFloat(item?.unit_price || 0),
            taxRate: parseFloat(item?.tax_rate || 0),
            taxAmount: parseFloat(item?.tax_amount || 0),
            lineTotal: parseFloat(item?.line_total || 0),
            notes: item?.notes,
            item: item?.items ? {
              itemName: item?.items?.item_name,
              itemNumber: item?.items?.item_number,
              unitOfMeasure: item?.items?.unit_of_measure
            } : null
          })) || []
      };
    } catch (error) {
      console.error('Error in creditNotesService.getById:', error);
      throw error;
    }
  },

  /**
   * Utility function to get user-friendly status display
   * @param {string} status - Credit note status
   * @returns {string} Formatted status display
   */
  getStatusDisplay(status) {
    const statusMap = {
      [CREDIT_NOTE_STATUS?.DRAFT]: 'Draft',
      [CREDIT_NOTE_STATUS?.ISSUED]: 'Issued',
      [CREDIT_NOTE_STATUS?.APPLIED]: 'Applied',
      [CREDIT_NOTE_STATUS?.CANCELLED]: 'Cancelled',
      [CREDIT_NOTE_STATUS?.VOID]: 'Void'
    };
    return statusMap?.[status] || 'Unknown';
  },

  /**
   * Utility function to get status color for UI
   * @param {string} status - Credit note status
   * @returns {string} Tailwind CSS color class
   */
  getStatusColor(status) {
    const colorMap = {
      [CREDIT_NOTE_STATUS?.DRAFT]: 'bg-gray-100 text-gray-800',
      [CREDIT_NOTE_STATUS?.ISSUED]: 'bg-blue-100 text-blue-800',
      [CREDIT_NOTE_STATUS?.APPLIED]: 'bg-green-100 text-green-800',
      [CREDIT_NOTE_STATUS?.CANCELLED]: 'bg-yellow-100 text-yellow-800',
      [CREDIT_NOTE_STATUS?.VOID]: 'bg-red-100 text-red-800'
    };
    return colorMap?.[status] || 'bg-gray-100 text-gray-800';
  },

  /**
   * Gets credit note statistics for dashboard
   * @param {string} organizationId - Organization identifier
   * @returns {Promise<Object>} Credit note statistics
   */
  async getStatistics(organizationId) {
    try {
      const user = await validateAuth();
      
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      const { data, error } = await supabase
        ?.from('credit_notes')
        ?.select('status, total_credit_amount, remaining_credit, applied_amount')
        ?.eq('organization_id', organizationId);

      if (error) handleDatabaseError(error, 'Fetching credit note statistics');

      const stats = {
        total: data?.length || 0,
        totalCreditAmount: 0,
        totalAppliedAmount: 0,
        totalRemainingCredit: 0,
        byStatus: {
          [CREDIT_NOTE_STATUS?.DRAFT]: 0,
          [CREDIT_NOTE_STATUS?.ISSUED]: 0,
          [CREDIT_NOTE_STATUS?.APPLIED]: 0,
          [CREDIT_NOTE_STATUS?.CANCELLED]: 0,
          [CREDIT_NOTE_STATUS?.VOID]: 0
        }
      };

      data?.forEach(creditNote => {
        stats.totalCreditAmount += parseFloat(creditNote?.total_credit_amount || 0);
        stats.totalAppliedAmount += parseFloat(creditNote?.applied_amount || 0);
        stats.totalRemainingCredit += parseFloat(creditNote?.remaining_credit || creditNote?.total_credit_amount || 0);
        stats.byStatus[creditNote?.status] = (stats?.byStatus?.[creditNote?.status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error in creditNotesService.getStatistics:', error);
      throw error;
    }
  }
};

// Export constants for use in components
export default creditNotesService;